/**
 * Business-goal chooser modal.
 *
 * The operator picks one of 5 goals before the Predictive Segmentation
 * Agent runs. The agent re-weights candidate scores so cohorts aligned
 * to that goal float to the top — but nothing is DROPPED for goal
 * alignment, so the operator still sees the full mix.
 *
 * Five goals + a "balanced" escape hatch:
 *   💰 Revenue        — high-value, whales, upsell-ready
 *   🔁 Retention      — loyal, active, repeat behavior
 *   🔄 Reactivation   — lapsed, dormant, win-back
 *   🔻 Funnel         — drop-offs, cart-abandon, KYC-stuck
 *   📈 Engagement     — multi-channel, high-frequency
 *   🎯 Balanced       — no boost, just the deterministic mix
 *
 * The selection is purely local to this run — we don't persist a
 * tenant-level default. If the operator wants the same goal every
 * run they can choose it again; future enhancement: per-user
 * "remember last goal" via localStorage.
 */
import React, { useState, useEffect } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, Button, SimpleGrid, Box, Text, HStack, VStack,
  useColorModeValue, Badge, Spinner, Divider,
} from "@chakra-ui/react";
import { probePredictiveSegmentation } from "./aiAgentsClient";

export const BUSINESS_GOALS = [
  { id: "revenue",      icon: "💰", label: "Revenue",      description: "High-value buyers, whales, upsell-ready",   examples: "Top spenders · VIP tiers · p90/p99 amounts" },
  { id: "retention",    icon: "🔁", label: "Retention",    description: "Loyal, active, repeat behavior",             examples: "Active 30d · Power users · Frequent repeat" },
  { id: "reactivation", icon: "🔄", label: "Reactivation", description: "Lapsed, dormant, win-back",                  examples: "Silent 90d · Lapsed donors · Cart abandoners" },
  { id: "funnel",       icon: "🔻", label: "Funnel",       description: "Drop-offs, cart-abandon, KYC-stuck",          examples: "Viewed-not-bought · KYC pending · Onboarding stuck" },
  { id: "engagement",   icon: "📈", label: "Engagement",   description: "Multi-channel, high-frequency",                examples: "Power users · Cross-channel · 3+ visits / week" },
  { id: "balanced",     icon: "🎯", label: "Balanced",     description: "Mix of all dimensions — no specific goal",   examples: "Default · Industry-balanced · Discovery-driven" },
];

const STATUS_COLOR = {
  none:     { color: "red",    label: "missing" },
  thin:     { color: "orange", label: "thin" },
  moderate: { color: "yellow", label: "moderate" },
  strong:   { color: "green",  label: "strong" },
};

function ReadinessBanner({ readiness }) {
  if (!readiness) return null;
  const fams = readiness.families || {};
  const sigs = readiness.signals || {};
  const sm = readiness.summary || {};
  return (
    <Box mt={2} mb={4} p={3} border="1px solid" borderColor="purple.100"
         bg="purple.50" borderRadius="lg">
      <HStack mb={2} spacing={2}>
        <Text fontSize="sm" fontWeight="600" color="gray.800">
          Data readiness — {sm.headline || "—"}
        </Text>
      </HStack>
      <HStack spacing={2} mb={2} wrap="wrap">
        <Text fontSize="xs" color="gray.600">Families covered:</Text>
        <Badge colorScheme={STATUS_COLOR[sm.families_status]?.color || "gray"} variant="subtle">
          {sm.families_covered ?? 0}/{sm.families_total ?? 6}
        </Badge>
        {Object.entries(fams).map(([fam, info]) => (
          <Badge key={fam} colorScheme={STATUS_COLOR[info.status]?.color || "gray"} variant="outline" fontSize="10px">
            {fam}: {info.count}
          </Badge>
        ))}
      </HStack>
      <HStack spacing={2} wrap="wrap">
        <Text fontSize="xs" color="gray.600">Signals:</Text>
        {Object.entries(sigs).map(([s, info]) => (
          <Badge key={s} colorScheme={STATUS_COLOR[info.status]?.color || "gray"} variant="subtle" fontSize="10px">
            {s} {STATUS_COLOR[info.status]?.label || info.status}
          </Badge>
        ))}
      </HStack>
      {Array.isArray(readiness.advice) && readiness.advice.length > 0 && (
        <Box mt={2}>
          {readiness.advice.slice(0, 3).map((a, i) => (
            <Text key={i} fontSize="11px" color="gray.700" mt={0.5}>⚠ {a}</Text>
          ))}
        </Box>
      )}
    </Box>
  );
}

const BusinessGoalModal = ({ isOpen, onClose, onConfirm }) => {
  const [selected, setSelected] = useState("balanced");
  const [readiness, setReadiness] = useState(null);
  const [probeLoading, setProbeLoading] = useState(false);
  const [probeError, setProbeError] = useState(null);
  const cardBg = useColorModeValue("white", "gray.700");
  const cardActiveBg = useColorModeValue("purple.50", "purple.900");

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setProbeLoading(true); setProbeError(null);
    probePredictiveSegmentation()
      .then((out) => { if (!cancelled) setReadiness(out?.result?.readiness ?? null); })
      .catch((e) => { if (!cancelled) setProbeError(e?.message || "Probe failed"); })
      .finally(() => { if (!cancelled) setProbeLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen]);

  const handleConfirm = () => {
    onConfirm(selected);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Box w={8} h={8} borderRadius="md" bg="purple.500"
                 display="flex" alignItems="center" justifyContent="center" color="white">
              <span style={{ fontSize: 18 }}>✨</span>
            </Box>
            <VStack spacing={0} align="start">
              <Text fontSize="md" fontWeight="600">What outcome do you want to optimize?</Text>
              <Text fontSize="xs" color="gray.500" fontWeight="400">
                The AI will rank cohorts that drive your chosen goal first.
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={4}>
          {probeLoading && (
            <HStack p={3} bg="gray.50" borderRadius="md" mb={3}>
              <Spinner size="sm" color="purple.500" />
              <Text fontSize="xs" color="gray.600">Checking your tenant data readiness…</Text>
            </HStack>
          )}
          {probeError && (
            <Box p={3} bg="orange.50" borderRadius="md" mb={3}>
              <Text fontSize="xs" color="orange.700">⚠ Could not run readiness probe: {probeError}. You can still generate suggestions.</Text>
            </Box>
          )}
          {readiness && <ReadinessBanner readiness={readiness} />}
          <Divider mb={3} />
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
            {BUSINESS_GOALS.map((g) => {
              const isActive = selected === g.id;
              return (
                <Box
                  key={g.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelected(g.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setSelected(g.id); }}
                  cursor="pointer"
                  bg={isActive ? cardActiveBg : cardBg}
                  border="2px solid"
                  borderColor={isActive ? "purple.500" : "gray.200"}
                  borderRadius="lg"
                  p={3}
                  transition="all 120ms ease"
                  _hover={{ borderColor: isActive ? "purple.500" : "purple.200" }}
                >
                  <HStack spacing={3} align="start">
                    <Text fontSize="2xl">{g.icon}</Text>
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontWeight="600" fontSize="sm">{g.label}</Text>
                      <Text fontSize="xs" color="gray.600">{g.description}</Text>
                      <Text fontSize="11px" color="gray.500" fontStyle="italic">
                        e.g. {g.examples}
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              );
            })}
          </SimpleGrid>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button
            colorScheme="purple"
            onClick={handleConfirm}
            leftIcon={<span style={{ fontSize: 14 }}>✨</span>}
          >
            Generate suggestions
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BusinessGoalModal;
